import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import api, { leadApi, type Lead } from '../../lib/api'
import type { AccountAnchor } from '../../lib/api'

/**
 * Sales reps only book leads they can open. The leads list is already
 * scoped the same way; this keeps a secondary-assignee lead in the picker
 * and drops any other row the response included.
 *
 * Merchant search asks for `appointment_bookable`. A sales rep then
 * sees merchants whose account lead lists them, which is who they can
 * book. A code-only merchant stays off this list. A super admin can
 * book every merchant the list returns.
 */
function leadsForActor<T extends {
  assigned_to?: string | null
  assigned_to_secondary?: string | null
}>(
  leads: T[],
  user: { id: string; role: string } | null | undefined,
): T[] {
  if (user?.role !== 'sales_rep') return leads
  return leads.filter((lead) => (
    lead.assigned_to === user.id || lead.assigned_to_secondary === user.id
  ))
}

export interface SelectedAccount {
  anchor: AccountAnchor
  label: string
}

interface MerchantHit {
  id: string
  dba_name?: string | null
}

function leadLabel(lead: Lead): string {
  return lead.dba_name || lead.legal_name || 'Unnamed lead'
}

export function AccountSearchField({
  selected,
  onSelect,
}: {
  selected: SelectedAccount | null
  onSelect: (account: SelectedAccount) => void
}) {
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 200)
    return () => window.clearTimeout(timer)
  }, [query])

  const ready = debounced.length >= 2

  const leadsQuery = useQuery({
    queryKey: ['appointment-account-search', 'leads', debounced],
    queryFn: () => leadApi.list({ search: debounced, per_page: 6 }),
    enabled: ready,
  })

  const merchantsQuery = useQuery({
    queryKey: ['appointment-account-search', 'merchants', debounced],
    queryFn: async () => {
      const response = await api.get<{ data: MerchantHit[] }>('/merchants', {
        params: { search: debounced, per_page: 6, appointment_bookable: 1 },
      })
      return response.data
    },
    enabled: ready,
  })

  const results = useMemo(() => {
    const leads = leadsForActor(
      Array.isArray(leadsQuery.data?.data) ? leadsQuery.data.data : [],
      user,
    )
    const merchants = Array.isArray(merchantsQuery.data?.data) ? merchantsQuery.data.data : []

    return [
      ...leads.map((lead) => ({
        key: `lead-${lead.id}`,
        label: leadLabel(lead),
        meta: 'Lead',
        anchor: { kind: 'lead' as const, id: lead.id },
      })),
      ...merchants.map((merchant) => ({
        key: `merchant-${merchant.id}`,
        label: merchant.dba_name || 'Unnamed merchant',
        meta: 'Merchant',
        anchor: { kind: 'merchant' as const, id: merchant.id },
      })),
    ]
  }, [leadsQuery.data, merchantsQuery.data, user])

  const searching = ready && (leadsQuery.isFetching || merchantsQuery.isFetching)
  const failed = ready && leadsQuery.isError && (merchantsQuery.isError || !merchantsQuery.isFetching)

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">
        Search leads and merchants
        <input
          type="search"
          role="searchbox"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Type at least 2 characters"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      {selected && (
        <p className="text-sm text-slate-700">Selected: {selected.label}</p>
      )}
      {!ready ? (
        <p className="text-xs text-slate-500">Type at least 2 characters.</p>
      ) : failed ? (
        <p className="text-sm text-red-600" role="alert">Search is unavailable.</p>
      ) : searching && results.length === 0 ? (
        <p className="text-sm text-slate-500">Searching…</p>
      ) : results.length === 0 ? (
        <p className="text-sm text-slate-500">No matching leads or merchants.</p>
      ) : (
        <ul aria-label="Matching leads and merchants" className="max-h-48 space-y-1 overflow-y-auto">
          {results.map((result) => (
            <li key={result.key}>
              <button
                type="button"
                onClick={() => onSelect({ anchor: result.anchor, label: result.label })}
                className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm text-slate-800 hover:bg-slate-100"
              >
                <span>{result.label}</span>
                <span className="text-xs text-slate-500">{result.meta}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
